/**
 * A time of day you can type (9, 930p, 21:30, noon) or pick from a list of
 * half hours that opens on focus.
 */
import { Clock } from 'lucide-react';
import { useId } from 'react';

import { bareInputStyle, fieldBoxStyle } from './picker-styles';
import { formatTimeOfDay } from './time-of-day';
import { TimeSlotMenu } from './TimeSlotMenu';
import { useTimeField } from './use-time-field';

export interface TimeFieldProps {
  label: string;
  value: number;
  invalid?: boolean;
  width?: number;
  onChange: (minutes: number) => void;
}

export function TimeField({
  label,
  value,
  invalid = false,
  width = 132,
  onChange,
}: TimeFieldProps) {
  const listId = useId();
  const field = useTimeField(value, onChange);
  return (
    <div style={{ position: 'relative', width, flex: `0 0 ${width}px` }}>
      <label style={fieldBoxStyle(field.open, invalid || field.invalid)}>
        <Clock size={15} aria-hidden style={{ flex: '0 0 auto', color: 'var(--nb-muted)' }} />
        <input
          role="combobox"
          aria-label={label}
          aria-expanded={field.open}
          aria-controls={listId}
          aria-invalid={invalid || field.invalid}
          value={field.draft ?? formatTimeOfDay(value)}
          onFocus={(e) => {
            field.focus();
            e.target.select();
          }}
          onChange={(e) => field.type(e.target.value)}
          onBlur={field.commit}
          onKeyDown={field.onKeyDown}
          style={bareInputStyle}
        />
      </label>
      {field.open && (
        <TimeSlotMenu
          id={listId}
          current={field.current}
          typed={field.typed}
          width={width}
          onPick={field.pick}
        />
      )}
    </div>
  );
}
